import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { SITE } from "../lib/constants";
import { Menu, X } from "lucide-react";
import DarkModeToggle from "./DarkModeToggle";

const Header = () => {
  const { t, lang, setLanguage } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const navItems = [
    { id: "about", label: t.nav.about },
    { id: "specialties", label: t.nav.specialties },
    { id: "pathologies", label: lang === "fr" ? "Pathologies" : "Pathologies" },
    { id: "sport", label: t.nav.sport },
    { id: "locations", label: t.nav.locations },
    { id: "expertise", label: t.nav.expertise },
    { id: "blog", label: t.nav.blog },
  ];

  // Items hidden from the desktop nav for compactness, but kept in the mobile menu and footer
  const mobileOnlyNavItems = [
    { id: "raac", label: lang === "fr" ? "Récupération" : "Recovery" },
    { id: "media", label: lang === "fr" ? "Conférences" : "Lectures" },
    { id: "faq", label: t.nav.faq },
  ];

  const externalLinks = [
    { to: "/recherche", label: lang === "fr" ? "Centre bibliographique" : "Bibliography center" },
    { to: "/publications", label: "Publications" },
  ];

  const handleAnchor = (e, id) => {
    if (location.pathname !== "/") return; // regular navigation handled by Link
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 72;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? "border-b border-border shadow-sm" : "border-b border-border/40"
      }`}
      style={{ backgroundColor: "#ffffff", color: "#0f172a" }}
      data-theme="light"
    >
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-8 lg:px-10 flex h-16 md:h-20 items-center justify-between gap-2 sm:gap-3">
        {/* Logo */}
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-3 shrink-0"
          aria-label="IMOTEpS — Accueil"
        >
          <img
            src={SITE.logo}
            alt="IMOTEpS"
            className="h-8 sm:h-10 md:h-12 w-auto max-w-[100px] sm:max-w-[140px] object-contain"
            width="120"
            height="48"
          />
          <span className="hidden 2xl:flex flex-col leading-tight">
            <span className="font-display font-extrabold text-navy text-sm tracking-tight">
              Dr Richard ASWAD
            </span>
            <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">
              IMOTEpS · Marseille
            </span>
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden xl:flex items-center gap-4 2xl:gap-5 flex-1 justify-end" aria-label="Navigation principale">
          {navItems.map((item) => (
            <Link
              key={item.id}
              to={`/#${item.id}`}
              data-testid={`nav-link-${item.id}`}
              onClick={(e) => handleAnchor(e, item.id)}
              className="text-[13px] 2xl:text-sm font-semibold text-foreground/80 hover:text-[#FF5A1F] transition-colors whitespace-nowrap"
            >
              {item.label}
            </Link>
          ))}
          {externalLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              data-testid={`nav-link-${link.to.replace(/\//g, "")}`}
              className="text-[13px] 2xl:text-sm font-semibold text-foreground/80 hover:text-[#FF5A1F] transition-colors whitespace-nowrap"
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* Right actions */}
        <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3 shrink-0">
          <DarkModeToggle />
          {/* Language switch */}
          <div
            className="inline-flex items-center rounded-full border border-border bg-white/60 p-0.5 text-[10px] sm:text-[11px] font-bold shrink-0"
            role="group"
            aria-label="Language"
          >
            <button
              type="button"
              data-testid="lang-switch-fr"
              onClick={() => setLanguage("fr")}
              className={`px-2.5 py-1 rounded-full transition-colors ${
                lang === "fr"
                  ? "bg-[#0A192F] text-white"
                  : "text-foreground/70 hover:text-foreground"
              }`}
            >
              FR
            </button>
            <button
              type="button"
              data-testid="lang-switch-en"
              onClick={() => setLanguage("en")}
              className={`px-2.5 py-1 rounded-full transition-colors ${
                lang === "en"
                  ? "bg-[#0A192F] text-white"
                  : "text-foreground/70 hover:text-foreground"
              }`}
            >
              EN
            </button>
          </div>

          <a
            href="#contact"
            data-testid="nav-cta-rdv"
            onClick={(e) => handleAnchor(e, "contact")}
            className="hidden sm:inline-flex btn-primary !px-4 !py-2 text-xs"
          >
            <i className="fa-solid fa-calendar-check text-[13px]" aria-hidden="true"></i>
            {t.nav.bookRdv}
          </a>

          {/* Mobile menu button */}
          <button
            type="button"
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen((v) => !v)}
            className="xl:hidden inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-navy hover:bg-muted"
            aria-label="Ouvrir le menu"
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="xl:hidden bg-white border-t border-border shadow-xl"
        >
          <nav className="container-page py-4 flex flex-col gap-1" aria-label="Menu mobile">
            {[...navItems, ...mobileOnlyNavItems].map((item) => (
              <Link
                key={item.id}
                to={`/#${item.id}`}
                data-testid={`mobile-nav-link-${item.id}`}
                onClick={(e) => handleAnchor(e, item.id)}
                className="px-3 py-3 text-[15px] font-semibold text-foreground/85 hover:text-[#FF5A1F] hover:bg-muted/60 rounded-md transition"
              >
                {item.label}
              </Link>
            ))}
            <div className="my-2 border-t border-border" />
            {externalLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`mobile-nav-${link.to.replace(/\//g, "")}`}
                className="px-3 py-3 text-[15px] font-semibold text-foreground/85 hover:text-[#FF5A1F] hover:bg-muted/60 rounded-md transition"
              >
                {link.label}
              </Link>
            ))}
            <a
              href="#contact"
              data-testid="mobile-nav-cta"
              onClick={(e) => handleAnchor(e, "contact")}
              className="btn-primary mt-3 w-full !py-3"
            >
              <i className="fa-solid fa-calendar-check" aria-hidden="true"></i>
              {t.nav.bookRdv}
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
